import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../utils/DataTable";
import ActionBar from "../utils/ActionBar";
import api from "../../api";

const Outreach = ({ showToast, currentUser }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("week");
  const [dateList, setDateList] = useState([]);
  const [showWeeklyTotals, setShowWeeklyTotals] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date()); // anchor for Week/MTD/YTD navigation
  const [view, setView] = useState('mine'); // 'mine' | 'team'
  const [teamRows, setTeamRows] = useState([]); // raw rows for team view
  const [teamMembers, setTeamMembers] = useState([]); // roster of team members

  useEffect(() => {
    // Build date list newest->oldest
    const now = new Date(currentDate);
    const toYmdLocal = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    let dates = [];
    if (range === "week") {
      const day = now.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const start = new Date(now);
      start.setDate(now.getDate() + mondayOffset);
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        dates.push(toYmdLocal(d));
      }
      dates.reverse();
    } else if (range === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(toYmdLocal(new Date(d)));
      }
      dates.reverse();
    } else {
      const start = new Date(now.getFullYear(), 0, 1);
      const today = new Date();
      const end = now.getFullYear() === today.getFullYear() ? today : new Date(now.getFullYear(), 11, 31);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(toYmdLocal(new Date(d)));
      }
      dates.reverse();
    }
    setDateList(dates);
  }, [range, currentDate]);

  useEffect(() => {
    const fetchData = async () => {
      if (dateList.length === 0) return;
      setLoading(true);
      try {
        const startDate = dateList[dateList.length - 1];
        const endDate = dateList[0];
        const [mineRes, teamRes] = await Promise.all([
          api.get(`/outreach/range?startDate=${startDate}&endDate=${endDate}`),
          api.get(`/outreach/team-range?startDate=${startDate}&endDate=${endDate}`)
        ]);
        if (mineRes?.data?.success && Array.isArray(mineRes.data.data)) {
          const existingByDate = new Map(mineRes.data.data.map(r => [r.date, r]));
          const full = dateList.map(date => {
            const found = existingByDate.get(date);
            return {
              id: date,
              date,
              calls: found ? found.calls : '',
              contacts: found ? found.contacts : '',
              appts_set: found ? found.appts_set : '',
              appts_today: found ? found.appts_today : '',
              sits: found ? found.sits : '',
              emails: found ? found.emails : '',
              responses: found ? found.responses : '',
            };
          });
          setRows(full);
        } else {
          setRows(dateList.map(date => ({ id: date, date, calls: '', contacts: '', appts_set: '', appts_today: '', sits: '', emails: '', responses: '' })));
        }
        if (teamRes?.data?.success && Array.isArray(teamRes.data.data)) {
          setTeamRows(teamRes.data.data);
        } else {
          setTeamRows([]);
        }
        // Fetch roster of team members (include even with no outreach rows)
        try {
          const roster = await api.get('/outreach/team-members');
          if (roster?.data?.success && Array.isArray(roster.data.data)) {
            setTeamMembers(roster.data.data);
          } else {
            setTeamMembers([]);
          }
        } catch (_) {
          setTeamMembers([]);
        }
      } catch (e) {
        console.error(e);
        showToast && showToast("Failed to load outreach", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateList, showToast]);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const dow = ['Su','Mo','Tu','We','Th','Fr','Sa'][d.getDay()] || '';
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${dow} ${m}/${day}`;
  };

  // Helpers placed before useMemo so they are initialized
  function getMondayOfWeek(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDay();
    const monday = new Date(date);
    monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    const y = monday.getFullYear();
    const m = String(monday.getMonth() + 1).padStart(2, '0');
    const d = String(monday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function formatWeekLabel(weekKey) {
    const monday = new Date(weekKey + 'T00:00:00');
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const m1 = monday.getMonth() + 1; const d1 = monday.getDate();
    const m2 = sunday.getMonth() + 1; const d2 = sunday.getDate();
    return `Week ${m1}/${d1}-${m2}/${d2}`;
  }

  const columnsMine = useMemo(() => {
    const editableNumericCell = (field) => ({ value, row, isEditing, updateCell }) => {
      const isTotals = row?.original?.isTotals || row?.original?.isWeeklyTotal;
      const style = { textAlign: 'center', fontWeight: isTotals ? 600 : undefined };
      if (isTotals) return <div style={style}>{value}</div>;
      if (isEditing) {
        return (
          <input
            type="number"
            value={value === undefined || value === null ? '' : value}
            onChange={(e) => updateCell(row.original.id, field, e.target.value)}
            style={{ width: '100%', textAlign: 'center' }}
          />
        );
      }
      return <div style={{ textAlign: 'center' }}>{value}</div>;
    };

    return [
      { Header: "Date", accessor: "date", width: 65, Cell: ({ value, row }) => {
        const isTotals = row?.original?.isTotals;
        const isWeeklyTotal = row?.original?.isWeeklyTotal;
        const style = { textAlign: 'center', fontWeight: isTotals ? 600 : undefined };
        const label = isTotals ? 'Totals' : (isWeeklyTotal && row?.original?.displayLabel ? row.original.displayLabel : formatDisplayDate(value));
        return <div style={style}>{label}</div>;
      } },
      { Header: "Calls", accessor: "calls", width: 65, Cell: editableNumericCell('calls') },
      { Header: "Contacts", accessor: "contacts", width: 65, Cell: editableNumericCell('contacts') },
      { Header: "Appts Set", accessor: "appts_set", width: 65, Cell: editableNumericCell('appts_set') },
      { Header: "Appts Today", accessor: "appts_today", width: 65, Cell: editableNumericCell('appts_today') },
      { Header: "Sits", accessor: "sits", width: 65, Cell: editableNumericCell('sits') },
      { Header: "Emails", accessor: "emails", width: 65, Cell: editableNumericCell('emails') },
      { Header: "Responses", accessor: "responses", width: 65, Cell: editableNumericCell('responses') },
    ];
  }, []);

  const metrics = [
    { key: 'calls', label: 'Calls' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'appts_set', label: 'Appts Set' },
    { key: 'appts_today', label: 'Appts Today' },
    { key: 'sits', label: 'Sits' },
    { key: 'emails', label: 'Emails' },
    { key: 'responses', label: 'Responses' },
  ];

  const columnsTeam = useMemo(() => {
    // Two-level header: top groups (Totals, then Mon..Sun or Week ranges), second row metric labels
    const metricColWidth = 65;
    const groupWidth = metrics.length * metricColWidth; // 7 columns per section

    const groups = [];
    groups.push({ Header: 'Name', accessor: 'name', width: 160 });

    // Totals group always first
    groups.push({
      Header: 'Totals',
      width: groupWidth,
      minWidth: groupWidth,
      columns: metrics.map(m => ({
        Header: m.label,
        accessor: `total_${m.key}`,
        width: metricColWidth,
        Cell: ({ value }) => <div style={{ textAlign: 'center', fontWeight: 600 }}>{value || 0}</div>
      }))
    });

    if (range === 'week') {
      // Monday -> Sunday left to right
      const monToSun = [...dateList].reverse();
      monToSun.forEach(dateStr => {
        groups.push({
          Header: formatDisplayDate(dateStr),
          width: groupWidth,
          minWidth: groupWidth,
          columns: metrics.map(m => ({
            Header: m.label,
            accessor: `${dateStr}_${m.key}`,
            width: metricColWidth,
            Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value === undefined || value === null || value === '' ? '' : value}</div>
          }))
        });
      });
    } else {
      // Month/YTD: show week totals groups (Mon-Sun)
      const weekKeysAsc = Array.from(new Set([...dateList].reverse().map(d => getMondayOfWeek(d))));
      weekKeysAsc.forEach(weekKey => {
        groups.push({
          Header: formatWeekLabel(weekKey),
          width: groupWidth,
          minWidth: groupWidth,
          columns: metrics.map(m => ({
            Header: m.label,
            accessor: `${weekKey}_${m.key}`,
            width: metricColWidth,
            Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value === undefined || value === null || value === '' ? '' : value}</div>
          }))
        });
      });
    }

    return groups;
  }, [dateList, range]);

  const handleCellUpdate = async (id, field, value) => {
    if (id === '__totals__' || String(id).startsWith('week-total-')) return; // ignore edits on totals/weekly rows
    try {
      const numeric = Number(value);
      const safe = Number.isFinite(numeric) ? numeric : 0;
      await api.post("/outreach/upsert", { date: id, updates: { [field]: safe } });
    } catch (e) {
      console.error(e);
      showToast && showToast("Failed to save", "error");
    }
  };

  const totalsRow = useMemo(() => {
    const sum = (key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
    return {
      id: '__totals__',
      isTotals: true,
      date: '',
      calls: sum('calls'),
      contacts: sum('contacts'),
      appts_set: sum('appts_set'),
      appts_today: sum('appts_today'),
      sits: sum('sits'),
      emails: sum('emails'),
      responses: sum('responses'),
    };
  }, [rows]);

  // getMondayOfWeek and formatWeekLabel defined above

  const tableDataMine = useMemo(() => {
    const baseRows = [...rows];
    if (showWeeklyTotals && (range === 'month' || range === 'ytd')) {
      const groups = {};
      baseRows.forEach(r => {
        const wk = getMondayOfWeek(r.date);
        if (!groups[wk]) groups[wk] = [];
        groups[wk].push(r);
      });
      const weekKeys = Object.keys(groups).sort((a, b) => new Date(b) - new Date(a));
      const finalData = [];
      weekKeys.forEach(weekKey => {
        const weekRows = groups[weekKey];
        const sum = (key) => weekRows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
        finalData.push({
          id: `week-total-${weekKey}`,
          isWeeklyTotal: true,
          displayLabel: formatWeekLabel(weekKey),
          date: '',
          calls: sum('calls'),
          contacts: sum('contacts'),
          appts_set: sum('appts_set'),
          appts_today: sum('appts_today'),
          sits: sum('sits'),
          emails: sum('emails'),
          responses: sum('responses'),
        });
        // keep daily rows in current order (already newest->oldest)
        finalData.push(...weekRows);
      });
      return [totalsRow, ...finalData];
    }
    return [totalsRow, ...baseRows];
  }, [rows, totalsRow, showWeeklyTotals, range]);

  const teamTableData = useMemo(() => {
    // Aggregate by user → per-day metrics and totals
    const users = new Map(); // userId -> { name, perDate map, totals }
    teamRows.forEach(r => {
      const uid = r.userId;
      if (!users.has(uid)) {
        users.set(uid, {
          userId: uid,
          name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || `User ${uid}`,
          totals: { calls: 0, contacts: 0, appts_set: 0, appts_today: 0, sits: 0, emails: 0, responses: 0 },
          byDate: new Map(),
          byWeek: new Map(),
        });
      }
      const u = users.get(uid);
      // Update totals
      u.totals.calls += Number(r.calls) || 0;
      u.totals.contacts += Number(r.contacts) || 0;
      u.totals.appts_set += Number(r.appts_set) || 0;
      u.totals.appts_today += Number(r.appts_today) || 0;
      u.totals.sits += Number(r.sits) || 0;
      u.totals.emails += Number(r.emails) || 0;
      u.totals.responses += Number(r.responses) || 0;
      // By date
      const d = r.entryDate;
      if (!u.byDate.has(d)) u.byDate.set(d, { calls: 0, contacts: 0, appts_set: 0, appts_today: 0, sits: 0, emails: 0, responses: 0 });
      const bd = u.byDate.get(d);
      bd.calls += Number(r.calls) || 0;
      bd.contacts += Number(r.contacts) || 0;
      bd.appts_set += Number(r.appts_set) || 0;
      bd.appts_today += Number(r.appts_today) || 0;
      bd.sits += Number(r.sits) || 0;
      bd.emails += Number(r.emails) || 0;
      bd.responses += Number(r.responses) || 0;

      // By week (for month/ytd)
      const wk = getMondayOfWeek(d);
      if (!u.byWeek.has(wk)) u.byWeek.set(wk, { calls: 0, contacts: 0, appts_set: 0, appts_today: 0, sits: 0, emails: 0, responses: 0 });
      const bw = u.byWeek.get(wk);
      bw.calls += Number(r.calls) || 0;
      bw.contacts += Number(r.contacts) || 0;
      bw.appts_set += Number(r.appts_set) || 0;
      bw.appts_today += Number(r.appts_today) || 0;
      bw.sits += Number(r.sits) || 0;
      bw.emails += Number(r.emails) || 0;
      bw.responses += Number(r.responses) || 0;
    });

    // Ensure all team members exist in map even if no outreach
    teamMembers.forEach(member => {
      if (!users.has(member.id)) {
        users.set(member.id, {
          userId: member.id,
          name: `${member.firstName || ''} ${member.lastName || ''}`.trim() || `User ${member.id}`,
          totals: { calls: 0, contacts: 0, appts_set: 0, appts_today: 0, sits: 0, emails: 0, responses: 0 },
          byDate: new Map(),
        });
      }
    });

    // Build rows
    const rowsOut = Array.from(users.values()).map(u => {
      const row = {
        id: `user-${u.userId}`,
        name: u.name,
      };
      // Ensure maps exist
      if (!(u.byDate instanceof Map)) u.byDate = new Map();
      if (!(u.byWeek instanceof Map)) u.byWeek = new Map();
      // totals
      metrics.forEach(m => {
        row[`total_${m.key}`] = u.totals[m.key] || 0;
      });
      if (range === 'week') {
        // per-day values Monday -> Sunday
        const ordered = [...dateList].reverse();
        ordered.forEach(dateStr => {
          const val = u.byDate ? u.byDate.get(dateStr) : undefined;
          metrics.forEach(m => {
            row[`${dateStr}_${m.key}`] = val ? (val[m.key] || 0) : '';
          });
        });
      } else {
        // per-week totals across range (oldest -> newest)
        const weekKeysAsc = Array.from(new Set([...dateList].reverse().map(d => getMondayOfWeek(d))));
        weekKeysAsc.forEach(wk => {
          const val = u.byWeek ? u.byWeek.get(wk) : undefined;
          metrics.forEach(m => {
            row[`${wk}_${m.key}`] = val ? (val[m.key] || 0) : '';
          });
        });
      }
      return row;
    });

    // Sort by name for stability
    rowsOut.sort((a,b) => String(a.name).localeCompare(String(b.name)));
    return rowsOut;
  }, [teamRows, dateList, teamMembers]);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, margin: '8px 0', alignItems: 'center' }}>
        <button className="nav-button" onClick={() => {
          const d = new Date(currentDate);
          if (range === 'week') d.setDate(d.getDate() - 7);
          else if (range === 'month') d.setMonth(d.getMonth() - 1);
          else d.setFullYear(d.getFullYear() - 1);
          setCurrentDate(d);
        }}>{"<"}</button>
        <button className={range === "week" ? "selected" : "unselected"} onClick={() => setRange("week")}>Week</button>
        <button className={range === "month" ? "selected" : "unselected"} onClick={() => setRange("month")}>MTD</button>
        <button className={range === "ytd" ? "selected" : "unselected"} onClick={() => setRange("ytd")}>YTD</button>
        <button className="nav-button" onClick={() => {
          const d = new Date(currentDate);
          if (range === 'week') d.setDate(d.getDate() + 7);
          else if (range === 'month') d.setMonth(d.getMonth() + 1);
          else d.setFullYear(d.getFullYear() + 1);
          setCurrentDate(d);
        }}>{">"}</button>
      </div>
      <ActionBar selectedCount={0} totalCount={0} entityName="" archivedView={false} hideCount={true}>
        <div className="action-buttons-right" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <button
            className={`icon-only ${view === 'team' ? 'active' : ''}`}
            onClick={() => setView(view === 'team' ? 'mine' : 'team')}
            title={view === 'team' ? 'Show My Outreach' : 'Show Team Outreach'}
            style={{ marginRight: 8 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 11c1.657 0 3-1.79 3-4s-1.343-4-3-4-3 1.79-3 4 1.343 4 3 4z" stroke="currentColor" strokeWidth="2" fill={view==='team'?'currentColor':'none'}/>
              <path d="M8 11c1.657 0 3-1.79 3-4S9.657 3 8 3 5 4.79 5 7s1.343 4 3 4z" stroke="currentColor" strokeWidth="2" fill={view==='team'?'currentColor':'none'}/>
              <path d="M2 21v-2c0-2.761 3.582-4 6-4s6 1.239 6 4v2" stroke="currentColor" strokeWidth="2"/>
              <path d="M22 21v-2c0-2.2-2.686-3.6-5-3.95" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>
          {(range === 'month' || range === 'ytd') && (
            <div 
              className={`weekly-totals-toggle icon-only ${showWeeklyTotals ? 'active' : ''}`}
              onClick={() => setShowWeeklyTotals(!showWeeklyTotals)}
              title={showWeeklyTotals ? 'Hide Weekly Totals' : 'Show Weekly Totals'}
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="eye-icon"
              >
                {showWeeklyTotals ? (
                  <path 
                    d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                ) : (
                  <>
                    <path 
                      d="m1 1 22 22 M9.88 9.88a3 3 0 1 0 4.24 4.24 M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68 M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5-1.28" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </>
                )}
              </svg>
            </div>
          )}
        </div>
      </ActionBar>
      {loading ? (
        <div style={{ padding: 20 }}>Loading…</div>
      ) : (
        <>
        <DataTable
          columns={view === 'mine' ? columnsMine : columnsTeam}
          data={view === 'mine' ? tableDataMine : teamTableData}
          onCellUpdate={handleCellUpdate}
          disablePagination={true}
          highlightRowOnEdit={true}
          key={`outreach-${view}-${range}-${showWeeklyTotals}`}
        />
        </>
      )}
    </div>
  );
};

export default Outreach;


